import React, {useEffect, useState} from 'react';
import ProjectEditorTemplate from './ProjectEditorTemplate';
import projectService from '../../services/ProjectService';
import {useNavigate} from 'react-router-dom';
import userService from '../../services/UserService';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import authService from '../../services/AuthService';
import axios from 'axios';
import {API_URL} from '../../App';
import cloudStorageFileService from "../../services/CloudStorageFileService";
import {matchIsValidColor} from 'mui-color-input'

const ProjectEditor = () => {

    const [id] = useState(
        window.location.pathname.split('/')[3] === 'create'
            ? undefined
            : window.location.pathname.split('/')[3]
    );

    useTitle([
        {name: 'Projekt Lista', url: '/dashboard/projects'},
        {name: 'Projekt', url: '/dashboard/projects/' + (id || 'create')}
    ])

    const [project, setProject] = useState({
        shouldCreateTemplate: true
    });

    const [budget, setBudget] = useState()
    const [message, setMessage] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [files, setFiles] = useState([]);
    const {sendAlertMessage} = useSnackbar()
    const [waitingForServer, setWaitingForServer] = useState(false)

    const handleExtension = () => () => {
        setExpanded(!expanded);
        if (!budget) getBudgetOfProject()
    }

    const navigate = useNavigate()

    useEffect(() => {
        if (!id) return
        retrieveProject()
        getCommentsOfProject()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const retrieveProject = () => {
        projectService.findById(id)
            .then(response => {
                if (response.priority == null) response.priority = "NORMAL"
                setProject(response)
            })
            .catch(error => console.error(error))
    }

    const getBudgetOfProject = () => {
        projectService.getBudgetOfProject(id)
            .then(response => setBudget(response))
            .catch(() => sendAlertMessage("Pénzügyi adatok lekérése sikertelen", "error"))
    };

    const [commentsTable, setCommentsTable] = useState({
        page: 0,
        rowsPerPage: 25,
        totalCount: 0
    })
    const getCommentsOfProject = () => {
        projectService.getComments(id, {page: commentsTable.page, size: commentsTable.rowsPerPage})
            .then(response => setCommentsTable((prev) => ({...prev, totalCount: response.totalElements, rows: response.content})))
            .catch(() => sendAlertMessage("Megjegyzések lekérése sikertelen", "error"))
    };
    useEffect(() => {
        if (!id) return
        getCommentsOfProject()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentsTable.page, commentsTable.rowsPerPage]);

    const [users, setUsers] = useState([]);
    useEffect(() => {
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
    }, []);

    const onChange = (event) => {
        const {name, value} = event.target;
        setProject((prevProject) => ({
            ...prevProject,
            [name]: value,
        }));
    };

    const onChangeColor = (newValue) => {
        if (!matchIsValidColor(newValue)) return;
        setProject((prevProject) => ({
            ...prevProject,
            color: newValue,
        }));
    };

    const onBudgetChange = (event) => {
        const {name, value} = event.target;
        setBudget((prevBudget) => ({
            ...prevBudget,
            [name]: value,
        }));
    };

    const onLogbookHandlerChange = (newValue) => {
        setProject((prevProject) => ({
            ...prevProject,
            logbookHandler: newValue,
        }));
    };


    const create = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(prepareRequest())], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.post(API_URL + '/projects', formData, {headers: headers});
            if (response) {
                navigate(-1);
                sendAlertMessage('Projekt sikeresen hozzáadva')
            }
        } catch (error) {
            sendAlertMessage('Projekt létrehozása sikertelen', 'error')
            setMessage(error === undefined ? 'Szerver nem elérhető' : error)
        }
        setWaitingForServer(false)
    };


    const update = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(prepareRequest())], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.patch(API_URL + '/projects/' + id, formData, {headers: headers});
            if (response) {
                sendAlertMessage('Projekt sikeresen mentve')
                setFiles([])
                retrieveProject()
            }
        } catch (error) {
            sendAlertMessage('Projekt mentése sikertelen', 'error')
            setMessage(error === undefined ? 'Szerver nem elérhető' : error.message)
        }
        setWaitingForServer(false)
    };

    const updateBudget = (e) => {
        e.preventDefault();
        projectService.updateProjectBudget(id, budget)
            .then(() => getBudgetOfProject())
            .catch(error => setMessage(error.message))
    };

    const prepareRequest = () => {
        const copy = structuredClone(project)
        copy.logbookHandlerId = project.logbookHandler?.id
        delete copy.logbookHandler
        return copy
    }

    const onChangeCheckbox = (event) => {
        const {checked, name} = event.target
        setProject((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const removeDocumentName = (fileId) => {
        projectService.deleteProjectFile(id, fileId)
            .then(() => {
                retrieveProject()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    };

    const updateFileDescription = (id, form) => {
        cloudStorageFileService.update(id, form)
            .then(() => {
                retrieveProject()
                sendAlertMessage('Fájl leírása sikeresen módosítva!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    }

    return (
        <ProjectEditorTemplate
            {...{
                id,
                project,
                budget,
                message,
                onChange,
                onChangeColor,
                onBudgetChange,
                onLogbookHandlerChange,
                create,
                update,
                updateBudget,
                users,
                handleExtension,
                expanded,
                commentsTable,
                setCommentsTable,
                onChangeCheckbox,
                handleFileChange,
                handleRemoveFile,
                removeDocumentName,
                files,
                sendAlertMessage,
                waitingForServer,
                updateFileDescription
            }}
        />
    );
};

export default ProjectEditor;
